import React, { useState, useEffect, useRef, useCallback } from 'react';
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorMessage from '../common/ErrorMessage';
import { logger } from '../../services/logger';
import { validator } from '../../services/validation';
import { performanceMonitor } from '../../services/performance';
import { DocumentAPI } from '../../services/api';

interface PDFViewerProps {
  documentId: string;
  pageCount: number;
  isAnonymous: boolean;
  permissions?: {
    canView: boolean;
    canDownload: boolean;
    canPrint: boolean;
    watermark?: boolean;
    allowedPages?: number[];
    deniedPages?: number[];
  };
}

interface PageCache {
  [pageNumber: number]: string;
}

const PDFViewer: React.FC<PDFViewerProps> = ({
  documentId,
  pageCount,
  isAnonymous,
  permissions,
}) => {
  const [pageCache, setPageCache] = useState<PageCache>({});
  const [loadingPages, setLoadingPages] = useState<Set<number>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [zoom, setZoom] = useState(1.0);
  const [error, setError] = useState('');
  const [showThumbnails, setShowThumbnails] = useState(true);
  const [pageInput, setPageInput] = useState('1');

  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<{ [page: number]: HTMLDivElement | null }>({});
  const pageStartTimes = useRef<{ [page: number]: number }>({});
  const observerRef = useRef<IntersectionObserver | null>(null);

  const correlationId = logger.getCorrelationId();

  // ── Permissions ──────────────────────────────────────────────────────────
  const isPageAccessible = useCallback((pageNum: number): boolean => {
    if (!validator.validatePageNumber(pageNum, pageCount, { correlationId })) return false;
    if (isAnonymous || !permissions) return true;
    if (permissions.deniedPages?.includes(pageNum)) return false;
    if (permissions.allowedPages && permissions.allowedPages.length > 0)
      return permissions.allowedPages.includes(pageNum);
    return permissions.canView;
  }, [isAnonymous, permissions, pageCount, correlationId]);

  // ── Analytics ────────────────────────────────────────────────────────────
  const trackPageDuration = useCallback(async (pageNum: number, duration: number) => {
    if (duration < 1000 || isAnonymous) return;
    if (!validator.validatePageNumber(pageNum, pageCount, { correlationId })) return;
    if (duration < 0 || duration > 3600000) return;
    try {
      await DocumentAPI.trackPageView(documentId, pageNum, duration);
    } catch { /* non-fatal */ }
  }, [documentId, isAnonymous, pageCount, correlationId]);

  // ── Page loading ─────────────────────────────────────────────────────────
  const loadPage = useCallback(async (pageNum: number) => {
    if (pageCache[pageNum] || loadingPages.has(pageNum)) return;
    if (!validator.validatePageNumber(pageNum, pageCount, { correlationId })) return;
    if (!isPageAccessible(pageNum)) return;

    setLoadingPages(prev => new Set(prev).add(pageNum));
    try {
      const blob = isAnonymous
        ? await DocumentAPI.getAnonymousDocumentPage(documentId, pageNum)
        : await DocumentAPI.getDocumentPage(documentId, pageNum, permissions?.watermark);
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      setPageCache(prev => ({ ...prev, [pageNum]: dataUrl }));
    } catch (err: any) {
      if (err.response?.status === 403)
        logger.warn('Page access denied', { correlationId, operation: 'loadPage', documentId, pageNum });
      else
        logger.error('Page load failed', { correlationId, operation: 'loadPage', documentId, pageNum, error: err.message });
    } finally {
      setLoadingPages(prev => { const s = new Set(prev); s.delete(pageNum); return s; });
    }
  }, [pageCache, loadingPages, pageCount, isAnonymous, documentId, permissions, isPageAccessible, correlationId]);

  // ── IntersectionObserver — track visible page ────────────────────────────
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        let maxRatio = 0;
        let visiblePage = currentPage;
        entries.forEach(entry => {
          const page = Number(entry.target.getAttribute('data-page'));
          if (entry.intersectionRatio > maxRatio) {
            maxRatio = entry.intersectionRatio;
            visiblePage = page;
          }
          // Track duration when page leaves viewport
          if (!entry.isIntersecting && pageStartTimes.current[page]) {
            const duration = Date.now() - pageStartTimes.current[page];
            trackPageDuration(page, duration);
            delete pageStartTimes.current[page];
          }
          // Start timing when page enters viewport
          if (entry.isIntersecting && !pageStartTimes.current[page]) {
            pageStartTimes.current[page] = Date.now();
            // Load adjacent pages
            loadPage(page);
            if (page + 1 <= pageCount) loadPage(page + 1);
            if (page + 2 <= pageCount) loadPage(page + 2);
          }
        });
        if (maxRatio > 0) {
          setCurrentPage(visiblePage);
          setPageInput(String(visiblePage));
        }
      },
      { root: scrollAreaRef.current, threshold: [0.1, 0.5, 0.9] }
    );

    Object.values(pageRefs.current).forEach(el => {
      if (el) observerRef.current!.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, [pageCount, trackPageDuration, loadPage]);

  // Re-observe when refs change
  const setPageRef = useCallback((pageNum: number, el: HTMLDivElement | null) => {
    pageRefs.current[pageNum] = el;
    if (el && observerRef.current) observerRef.current.observe(el);
  }, []);

  // ── Flush active timers on unmount / document change ─────────────────────
  // IntersectionObserver.disconnect() fires no callbacks, so any pages the
  // user was reading when they navigate away would never be tracked otherwise.
  useEffect(() => {
    return () => {
      if (isAnonymous) return;
      const now = Date.now();
      Object.entries(pageStartTimes.current).forEach(([pageStr, startTime]) => {
        const duration = now - startTime;
        if (duration >= 1000 && duration <= 3600000) {
          DocumentAPI.trackPageView(documentId, Number(pageStr), duration).catch(() => {});
        }
      });
      pageStartTimes.current = {};
    };
  }, [documentId, isAnonymous]);

  // ── Flush active timers when tab is hidden (switch tabs / minimize) ───────
  useEffect(() => {
    if (isAnonymous) return;
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'hidden') return;
      const now = Date.now();
      Object.entries(pageStartTimes.current).forEach(([pageStr, startTime]) => {
        const duration = now - startTime;
        if (duration >= 1000 && duration <= 3600000) {
          DocumentAPI.trackPageView(documentId, Number(pageStr), duration).catch(() => {});
          // Reset so we don't double-count if the tab becomes visible again
          pageStartTimes.current[Number(pageStr)] = now;
        }
      });
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [documentId, isAnonymous]);

  // ── Initial load ─────────────────────────────────────────────────────────
  useEffect(() => {
    setPageCache({});
    loadPage(1);
    if (pageCount >= 2) loadPage(2);
  }, [documentId, permissions?.watermark]);

  // ── Keyboard navigation ──────────────────────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') { e.preventDefault(); scrollToPage(Math.min(currentPage + 1, pageCount)); }
      else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') { e.preventDefault(); scrollToPage(Math.max(currentPage - 1, 1)); }
      else if (e.key === 'Home') { e.preventDefault(); scrollToPage(1); }
      else if (e.key === 'End') { e.preventDefault(); scrollToPage(pageCount); }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPage, pageCount]);

  // ── Scroll to page ───────────────────────────────────────────────────────
  const scrollToPage = (pageNum: number) => {
    const clamped = Math.max(1, Math.min(pageNum, pageCount));
    const el = pageRefs.current[clamped];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    loadPage(clamped);
  };

  // ── Download / Print ─────────────────────────────────────────────────────
  const handleDownload = async () => {
    if (!permissions?.canDownload) return;
    try {
      const url = await DocumentAPI.downloadDocument(documentId);
      const a = document.createElement('a');
      a.href = url; a.download = `document-${documentId}.pdf`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
    } catch (err: any) { setError(err.message || 'Download failed'); }
  };

  const handlePrint = async () => {
    if (!permissions?.canPrint) return;
    const data = pageCache[currentPage];
    if (!data) return;
    try {
      await DocumentAPI.validatePrintAccess(documentId);
      const w = window.open('', '_blank');
      if (w) {
        w.document.write(`<html><head><title>Print</title><style>body{margin:0;text-align:center}img{max-width:100%}@media print{body{padding:0}}</style></head><body><img src="${data}"/></body></html>`);
        w.document.close(); w.focus(); w.print();
      }
    } catch (err: any) { setError(err.message || 'Print failed'); }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  const accessiblePages = Array.from({ length: pageCount }, (_, i) => i + 1).filter(isPageAccessible);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: '#404040', fontFamily: 'var(--font-sans)' }}>

      {/* ── Toolbar ── */}
      <div style={{ background: '#323639', borderBottom: '1px solid #1a1a1a', padding: '6px 12px', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 12, color: '#fff' }}>

        {/* Thumbnail toggle */}
        <button
          onClick={() => setShowThumbnails(t => !t)}
          title="Toggle thumbnails"
          style={{ background: showThumbnails ? '#555' : 'transparent', border: 'none', color: '#ccc', cursor: 'pointer', padding: '4px 8px', borderRadius: 4, fontSize: 16 }}
        >☰</button>

        <div style={{ width: 1, height: 20, background: '#555' }} />

        {/* Page navigation */}
        <button onClick={() => scrollToPage(currentPage - 1)} disabled={currentPage <= 1}
          style={{ background: 'transparent', border: 'none', color: currentPage <= 1 ? '#666' : '#ccc', cursor: currentPage <= 1 ? 'default' : 'pointer', fontSize: 16, padding: '2px 6px' }}>‹</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
          <input
            type="number" min={1} max={pageCount}
            value={pageInput}
            onChange={e => setPageInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { const n = parseInt(pageInput); if (n >= 1 && n <= pageCount) scrollToPage(n); } }}
            onBlur={() => { const n = parseInt(pageInput); if (n >= 1 && n <= pageCount) scrollToPage(n); else setPageInput(String(currentPage)); }}
            style={{ width: 44, textAlign: 'center', background: '#555', border: '1px solid #666', color: '#fff', borderRadius: 3, padding: '2px 4px', fontSize: 13 }}
          />
          <span style={{ color: '#aaa' }}>/ {pageCount}</span>
        </div>
        <button onClick={() => scrollToPage(currentPage + 1)} disabled={currentPage >= pageCount}
          style={{ background: 'transparent', border: 'none', color: currentPage >= pageCount ? '#666' : '#ccc', cursor: currentPage >= pageCount ? 'default' : 'pointer', fontSize: 16, padding: '2px 6px' }}>›</button>

        <div style={{ width: 1, height: 20, background: '#555' }} />

        {/* Zoom */}
        <button onClick={() => setZoom(z => Math.max(0.5, z - 0.25))} disabled={zoom <= 0.5}
          style={{ background: 'transparent', border: 'none', color: zoom <= 0.5 ? '#666' : '#ccc', cursor: zoom <= 0.5 ? 'default' : 'pointer', fontSize: 18, padding: '2px 6px' }}>−</button>
        <span style={{ fontSize: 13, color: '#ccc', minWidth: 44, textAlign: 'center' }}>{Math.round(zoom * 100)}%</span>
        <button onClick={() => setZoom(z => Math.min(3.0, z + 0.25))} disabled={zoom >= 3.0}
          style={{ background: 'transparent', border: 'none', color: zoom >= 3.0 ? '#666' : '#ccc', cursor: zoom >= 3.0 ? 'default' : 'pointer', fontSize: 18, padding: '2px 6px' }}>+</button>
        <button onClick={() => setZoom(1.0)}
          style={{ background: 'transparent', border: 'none', color: '#aaa', cursor: 'pointer', fontSize: 12, padding: '2px 6px' }}>Reset</button>

        <div style={{ flex: 1 }} />

        {/* Actions */}
        {!isAnonymous && permissions?.canDownload && (
          <button onClick={handleDownload}
            style={{ background: '#4f46e5', border: 'none', color: '#fff', cursor: 'pointer', padding: '4px 12px', borderRadius: 4, fontSize: 13 }}>
            ⬇ Download
          </button>
        )}
        {!isAnonymous && permissions?.canPrint && (
          <button onClick={handlePrint} disabled={!pageCache[currentPage]}
            style={{ background: 'transparent', border: '1px solid #555', color: '#ccc', cursor: 'pointer', padding: '4px 10px', borderRadius: 4, fontSize: 13 }}>
            🖨 Print
          </button>
        )}

        {/* Permission badges */}
        {!isAnonymous && permissions && (
          <div style={{ display: 'flex', gap: 4, marginLeft: 4 }}>
            {permissions.allowedPages && permissions.allowedPages.length > 0 && (
              <span style={{ background: '#f59e0b', color: '#000', fontSize: 11, padding: '2px 6px', borderRadius: 10 }}>
                Pages: {permissions.allowedPages.join(', ')}
              </span>
            )}
            {permissions.deniedPages && permissions.deniedPages.length > 0 && (
              <span style={{ background: '#ef4444', color: '#fff', fontSize: 11, padding: '2px 6px', borderRadius: 10 }}>
                Restricted: {permissions.deniedPages.join(', ')}
              </span>
            )}
          </div>
        )}
      </div>

      {error && (
        <div style={{ flexShrink: 0, padding: '8px 16px', background: '#2a1a1a' }}>
          <ErrorMessage message={error} onDismiss={() => setError('')} />
        </div>
      )}

      {/* ── Body: thumbnails + pages ── */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* Thumbnail sidebar */}
        {showThumbnails && (
          <div style={{ width: 120, background: '#2b2b2b', borderRight: '1px solid #1a1a1a', overflowY: 'auto', flexShrink: 0, padding: '8px 0' }}>
            {Array.from({ length: pageCount }, (_, i) => i + 1).map(pageNum => {
              const accessible = isPageAccessible(pageNum);
              const isCurrent = pageNum === currentPage;
              const cached = pageCache[pageNum];
              return (
                <div
                  key={pageNum}
                  onClick={() => accessible && scrollToPage(pageNum)}
                  style={{
                    margin: '0 8px 8px',
                    cursor: accessible ? 'pointer' : 'not-allowed',
                    border: isCurrent ? '2px solid #4f46e5' : '2px solid transparent',
                    borderRadius: 4,
                    overflow: 'hidden',
                    opacity: accessible ? 1 : 0.35,
                    background: '#3a3a3a',
                  }}
                  title={accessible ? `Page ${pageNum}` : `Page ${pageNum} — restricted`}
                >
                  {cached ? (
                    <img src={cached} alt={`Page ${pageNum}`} style={{ width: '100%', display: 'block' }} />
                  ) : (
                    <div style={{ height: 90, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#888', fontSize: 11 }}>
                      {loadingPages.has(pageNum) ? <LoadingSpinner size="small" inline /> : (accessible ? '📄' : '🚫')}
                    </div>
                  )}
                  <div style={{ textAlign: 'center', fontSize: 11, color: isCurrent ? '#a5b4fc' : '#888', padding: '2px 0 4px' }}>
                    {pageNum}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Scrollable page area */}
        <div
          ref={scrollAreaRef}
          style={{ flex: 1, overflowY: 'auto', background: '#525659', padding: '16px 0' }}
        >
          {Array.from({ length: pageCount }, (_, i) => i + 1).map(pageNum => {
            const accessible = isPageAccessible(pageNum);
            const cached = pageCache[pageNum];
            const isLoading = loadingPages.has(pageNum);

            return (
              <div
                key={pageNum}
                data-page={pageNum}
                ref={el => setPageRef(pageNum, el)}
                style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}
              >
                <div style={{
                  background: 'white',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.5)',
                  width: `${Math.round(816 * zoom)}px`,
                  minHeight: 280,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  transition: 'width 0.15s ease',
                }}>
                  {cached ? (
                    <img
                      src={cached}
                      alt={`Page ${pageNum}`}
                      style={{ display: 'block', width: '100%', height: 'auto' }}
                      onError={() => setError(`Failed to display page ${pageNum}`)}
                    />
                  ) : !accessible ? (
                    <div style={{ padding: 40, textAlign: 'center', color: '#999', minWidth: 400 }}>
                      <div style={{ fontSize: 40, marginBottom: 12 }}>🚫</div>
                      <div style={{ fontWeight: 600, color: '#dc3545' }}>Page {pageNum} — Restricted</div>
                      {permissions?.allowedPages && (
                        <div style={{ fontSize: 13, marginTop: 8 }}>
                          Accessible pages: {permissions.allowedPages.join(', ')}
                        </div>
                      )}
                    </div>
                  ) : isLoading ? (
                    <div style={{ padding: 60, minWidth: 400 }}>
                      <LoadingSpinner message={`Loading page ${pageNum}…`} />
                    </div>
                  ) : (
                    <div style={{ padding: 60, minWidth: 400, textAlign: 'center', color: '#aaa' }}>
                      Page {pageNum}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Keyboard hint */}
      <div style={{ background: '#323639', borderTop: '1px solid #1a1a1a', padding: '4px 12px', fontSize: 11, color: '#666', flexShrink: 0 }}>
        ↑↓ or ←→ to navigate · Home/End for first/last page
      </div>
    </div>
  );
};

export default PDFViewer;
